import { Form } from "@inertiajs/react"
import { AlertTriangle } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * Brand-side `Flag issue` dialog (UX Spec §2.4).
 *
 * Wraps Inertia `<Form>` (Crew rule). Hidden `event=flag_issue` + `Textarea`
 * named `issue_note` (server-side validated 1..500 chars). Submit button
 * disabled until the textarea is non-empty AND ≤500 chars (UI candy — the
 * server validates either way and surfaces field-level errors via
 * Inertia's `errors.issue_note`).
 *
 * A11y:
 *   - Textarea is `aria-required="true"` with `aria-describedby` pointing
 *     at the helper + counter.
 *   - First focus on Cancel per UX Spec §6 — destructive default.
 */
interface FlagIssueDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

const MAX_NOTE_CHARS = 500

export function FlagIssueDialog({ row, missionId }: FlagIssueDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState("")
  const trimmedLength = note.trim().length
  const overLimit = note.length > MAX_NOTE_CHARS
  const submitDisabled = trimmedLength === 0 || overLimit
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setNote("")
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={t("collabs.flag_issue.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-flag-issue-trigger"
          >
            <AlertTriangle aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.flag_issue.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-flag-issue-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.flag_issue.title")}</DialogTitle>
          <DialogDescription>{t("collabs.flag_issue.body")}</DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => handleOpenChange(false)}
        >
          {({ processing, errors }) => {
            // Inertia surfaces `errors[field]` as `string | string[] |
            // undefined`. Rails sends a single string in v1, but type-
            // check honestly so a future multi-error shape doesn't crash.
            const rawError = errors?.issue_note
            const fieldError = Array.isArray(rawError) ? rawError[0] : rawError
            return (
              <div className="flex flex-col gap-3">
                <input type="hidden" name="event" value="flag_issue" />
                {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
                <ReturnToFields />
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="flag-issue-note">
                    {t("collabs.flag_issue.note_label")}
                  </Label>
                  <Textarea
                    id="flag-issue-note"
                    name="issue_note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    aria-required="true"
                    aria-describedby="flag-issue-note-helper flag-issue-note-counter"
                    aria-invalid={fieldError ? "true" : undefined}
                    placeholder={t("collabs.flag_issue.note_placeholder")}
                    rows={4}
                    data-testid="brand-mission-collab-flag-issue-textarea"
                  />
                  <div className="flex items-center justify-between text-xs">
                    <span
                      id="flag-issue-note-helper"
                      className="text-muted-foreground"
                    >
                      {t("collabs.flag_issue.note_helper")}
                    </span>
                    <span
                      id="flag-issue-note-counter"
                      className={cn(
                        "tabular-nums",
                        overLimit
                          ? "text-destructive font-medium"
                          : "text-muted-foreground",
                      )}
                      data-testid="brand-mission-collab-flag-issue-counter"
                    >
                      {t("collabs.flag_issue.counter", {
                        count: note.length,
                      })}
                    </span>
                  </div>
                  {fieldError && (
                    <p
                      role="alert"
                      aria-live="polite"
                      className="text-destructive text-xs"
                      data-testid="brand-mission-collab-flag-issue-error"
                    >
                      {fieldError}
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    autoFocus
                    data-testid="brand-mission-collab-flag-issue-cancel"
                  >
                    {t("collabs.flag_issue.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={processing || submitDisabled}
                    data-testid="brand-mission-collab-flag-issue-confirm"
                  >
                    {t("collabs.flag_issue.confirm")}
                  </Button>
                </DialogFooter>
              </div>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
